import React, { useState, useEffect, useContext } from 'react';
import {
    Box, Typography, Button, Grid, Card, CardContent, CardActions,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, Chip, IconButton, Tooltip,
    CircularProgress, InputAdornment, Pagination, Switch, FormControlLabel, Paper,
    MenuItem
} from '@mui/material';
import { listModel, ModelKey } from '../config/aiConfig';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddIcon from '@mui/icons-material/Add';
import SchoolIcon from '@mui/icons-material/School';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { AuthContext } from '../context/AuthContext';
import { designTokens } from '../theme/designTokens';
import { useToast } from '../context/ToastContext';

interface Course {
    _id: string;
    title: string;
    description: string;
    sections: any[];
}

const CourseList = () => {
    const [courseList, setCourseList] = useState<Course[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [open, setOpen] = useState(false);
    const [newCourse, setNewCourse] = useState({ title: '', description: '' });
    const navigate = useNavigate();
    const auth = useContext(AuthContext);
    const { showToast } = useToast();

    // Delete confirmation states (double modal)
    const [deleteStep, setDeleteStep] = useState<0 | 1 | 2>(0); // 0: closed, 1: first confirm, 2: final confirm
    const [courseToDelete, setCourseToDelete] = useState<Course | null>(null);
    const [deleting, setDeleting] = useState(false);

    // AI Generation State
    const [isAiMode, setIsAiMode] = useState(false);
    const [aiTopic, setAiTopic] = useState('');
    const [aiInstructions, setAiInstructions] = useState('');
    const [aiDescription, setAiDescription] = useState('');
    const [modelChoose, setModelChoose] = useState<ModelKey>('default');
    const [creating, setCreating] = useState(false);

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => {
            setPage(1); // Reset to page 1 on search
            fetchCourses(1, searchQuery);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    // Fetch on page change
    useEffect(() => {
        fetchCourses(page, searchQuery);
    }, [page]);

    const fetchCourses = async (pageNum: number, search: string) => {
        setLoading(true);
        try {
            // Removed client-side caching to fix QuotaExceededError
            const res = await api.get(`/api/course/courses?page=${pageNum}&limit=9&search=${search}`);

            if (res.data && Array.isArray(res.data.courses)) {
                setCourseList(res.data.courses);
                setTotalPages(res.data.totalPages);
            } else {
                setCourseList([]);
            }
        } catch (err) {
            console.error("Error fetching courses", err);
            setCourseList([]);
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async () => {
        setCreating(true);
        try {
            await api.post('/api/course/course', newCourse);
            setOpen(false);
            setNewCourse({ title: '', description: '' });
            fetchCourses(1, '');
        } catch (err) {
            console.error("Error creating course", err);
        } finally {
            setCreating(false);
        }
    };

    const handleAiCreate = async () => {
        if (!aiTopic.trim()) return;
        setCreating(true);
        try {
            const res = await api.post('/api/course/ai-generate', {
                topic: aiTopic,
                description: '',
                instructions: aiInstructions,
                modelChoose: modelChoose
            });
            setOpen(false);
            setAiTopic('');
            setAiInstructions('');
            // Redirect to course builder
            if (res.data.courseId) {
                navigate(`/course/${res.data.courseId}`);
            }
        } catch (err: any) {
            console.error("Error generating course with AI", err);
            if (err.response && err.response.status === 429) {
                showToast("Quota OpenAI dépassé. Veuillez réessayer plus tard.", "error");
            } else {
                showToast("Erreur lors de la génération du cours", "error");
            }
        } finally {
            setCreating(false);
        }
    };

    const handleOpenDeleteModal = (course: Course) => {
        setCourseToDelete(course);
        setDeleteStep(1);
    };

    const handleCancelDelete = () => {
        setDeleteStep(0);
        setCourseToDelete(null);
    };

    const handleConfirmFirstStep = () => {
        setDeleteStep(2);
    };

    const handleDeleteCourse = async () => {
        if (!courseToDelete) return;
        setDeleting(true);
        try {
            await api.delete(`/api/course/course/${courseToDelete._id}`);
            setDeleteStep(0);
            setCourseToDelete(null);
            fetchCourses(page, searchQuery);
        } catch (err) {
            console.error("Error deleting course", err);
        } finally {
            setDeleting(false);
        }
    };

    // Calculate statistics
    const totalCourses = courseList.length;
    const totalSections = courseList.reduce((acc, c) => acc + (c.sections?.length || 0), 0);

    return (
        <Box sx={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e9f2 100%)', // Marketing Page Background
            p: { xs: 2, md: 4 }
        }}>
            {/* Hero Header */}
            <Paper
                elevation={0}
                sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', // Marketing Page Gradient
                    borderRadius: 4,
                    p: { xs: 3, md: 5 },
                    mb: 4,
                    color: 'white',
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                {/* Decorative circles */}
                <Box sx={{
                    position: 'absolute',
                    top: -50,
                    right: -50,
                    width: 200,
                    height: 200, // Reduced from 2000 to match Marketing
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.1)',
                }} />
                <Box sx={{
                    position: 'absolute',
                    bottom: -30,
                    right: 100,
                    width: 120,
                    height: 120,
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.08)',
                }} />

                <Grid container spacing={4} alignItems="center">
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Box sx={{
                                bgcolor: 'rgba(255,255,255,0.2)',
                                p: 1.5,
                                borderRadius: '50%',
                                backdropFilter: 'blur(10px)',
                                display: 'flex'
                            }}>
                                <SchoolIcon sx={{ fontSize: 32, color: 'white' }} />
                            </Box>
                            <Box>
                                <Typography variant="h4" sx={{ fontWeight: 700, fontFamily: 'Quicksand, sans-serif' }}>
                                    Gestion des Cours
                                </Typography>
                                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                                    Créez et gérez vos contenus pédagogiques
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <TextField
                                placeholder="Rechercher un cours..."
                                size="small"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{ color: 'rgba(255,255,255,0.7)' }} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    bgcolor: 'rgba(255,255,255,0.15)',
                                    borderRadius: 2,
                                    '& .MuiOutlinedInput-root': {
                                        color: 'white',
                                        '& fieldset': { borderColor: 'transparent' },
                                        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                                        '&.Mui-focused fieldset': { borderColor: 'white' },
                                    },
                                    '& .MuiInputBase-input::placeholder': {
                                        color: 'rgba(255,255,255,0.7)',
                                    },
                                    minWidth: 250
                                }}
                            />
                            {auth?.user?.role !== 'learner' && (
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={() => setOpen(true)}
                                    sx={{
                                        bgcolor: 'white',
                                        color: '#667eea',
                                        fontWeight: 700,
                                        px: 3,
                                        py: 1,
                                        borderRadius: 3,
                                        textTransform: 'none',
                                        boxShadow: '0 4px 14px rgba(0,0,0,0.2)',
                                        '&:hover': {
                                            bgcolor: 'rgba(255,255,255,0.95)',
                                            transform: 'translateY(-2px)',
                                        }
                                    }}
                                >
                                    Créer un Cours
                                </Button>
                            )}
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 6 }}>
                                <Box sx={{
                                    bgcolor: 'rgba(255,255,255,0.15)',
                                    backdropFilter: 'blur(10px)',
                                    borderRadius: 3,
                                    p: 2,
                                    textAlign: 'center',
                                }}>
                                    <Typography variant="h3" sx={{ fontWeight: 700 }}>{loading ? '-' : totalCourses}</Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>Cours Totaux</Typography>
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 6 }}>
                                <Box sx={{
                                    bgcolor: 'rgba(255,255,255,0.15)',
                                    backdropFilter: 'blur(10px)',
                                    borderRadius: 3,
                                    p: 2,
                                    textAlign: 'center',
                                }}>
                                    <Typography variant="h3" sx={{ fontWeight: 700 }}>{loading ? '-' : totalSections}</Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>Sections Totales</Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Paper>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                    <CircularProgress size={60} thickness={4} />
                </Box>
            ) : (
                <>
                    <Grid container spacing={4}>
                        {courseList.map((course) => (
                            <Grid size={{ xs: 12, md: 6, lg: 4 }} key={course._id}>
                                <Card sx={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    borderRadius: 4,
                                    overflow: 'hidden', // Changed from visible to hidden for internal content
                                    position: 'relative',
                                    transition: 'all 0.3s ease',
                                    border: '1px solid rgba(0,0,0,0.08)',
                                    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                                    '&:hover': {
                                        transform: 'translateY(-8px)',
                                        boxShadow: '0 12px 40px rgba(102, 126, 234, 0.25)',
                                    },
                                }}>
                                    {/* Header Gradient */}
                                    <Box sx={{
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        p: 3,
                                        color: 'white',
                                        position: 'relative'
                                    }}>
                                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
                                            {course.title}
                                        </Typography>
                                        <Chip
                                            label={`${course.sections?.length || 0} Sections`}
                                            size="small"
                                            sx={{
                                                bgcolor: 'rgba(255,255,255,0.2)',
                                                color: 'white',
                                                backdropFilter: 'blur(5px)',
                                                fontWeight: 600
                                            }}
                                        />
                                    </Box>

                                    <CardContent sx={{ flexGrow: 1, p: 3 }}>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            component="div"
                                            sx={{
                                                mb: 2,
                                                overflow: 'hidden',
                                                display: '-webkit-box',
                                                WebkitLineClamp: 3,
                                                WebkitBoxOrient: 'vertical',
                                                lineHeight: 1.6,
                                                '& p': { m: 0 },
                                                '& img': { display: 'none' } // Hide images in preview
                                            }}
                                            dangerouslySetInnerHTML={{ __html: course.description || "Aucune description" }}
                                        />
                                    </CardContent>

                                    <CardActions sx={{
                                        p: 2,
                                        borderTop: '1px solid',
                                        borderColor: 'divider',
                                        justifyContent: 'space-between',
                                        bgcolor: 'rgba(249, 250, 252, 0.5)'
                                    }}>
                                        <Box>
                                            <Tooltip title="Voir le cours">
                                                <IconButton
                                                    onClick={() => window.open(`/learner/course/${course._id}`, '_blank')}
                                                    size="small"
                                                    sx={{ color: '#667eea', '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.1)' } }}
                                                >
                                                    <VisibilityIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            {auth?.user?.role === 'super-admin' && (
                                                <Tooltip title="Supprimer">
                                                    <IconButton
                                                        onClick={() => handleOpenDeleteModal(course)}
                                                        size="small"
                                                        sx={{ color: '#f44336', '&:hover': { bgcolor: 'rgba(244, 67, 54, 0.1)' } }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Box>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            onClick={() => navigate(`/course/${course._id}`)}
                                            sx={{
                                                borderRadius: 2,
                                                textTransform: 'none',
                                                fontWeight: 600,
                                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                boxShadow: 'none',
                                                '&:hover': {
                                                    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                                                }
                                            }}
                                        >
                                            Modifier
                                        </Button>
                                    </CardActions>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>

                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                        <Pagination
                            count={totalPages}
                            page={page}
                            onChange={(e, value) => setPage(value)}
                            color="primary"
                            size="large"
                            sx={{
                                '& .MuiPaginationItem-root': {
                                    borderRadius: '50%',
                                    fontWeight: 700,
                                    '&.Mui-selected': {
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        color: 'white',
                                        boxShadow: '0 4px 10px rgba(102, 126, 234, 0.3)'
                                    }
                                }
                            }}
                        />
                    </Box>
                </>
            )}

            {/* Create/AI Modal - Kept same logic but improved styles */}
            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                PaperProps={{
                    sx: { borderRadius: 3, backdropFilter: 'blur(10px)', bgcolor: 'rgba(255,255,255,0.9)' }
                }}
            >
                <DialogTitle sx={{
                    fontWeight: 700,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    pb: 3
                }}>
                    Créer un nouveau Cours
                </DialogTitle>
                <DialogContent sx={{
                    mt: 3,
                    ml: 3,
                    mr: 3
                }}>
                    <Box sx={{
                        mb: 3,
                        p: 2,
                        borderRadius: 3,
                        bgcolor: '#f8f9fa',
                        border: '1px solid #e9ecef',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}>
                        <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Mode de création</Typography>
                            <Typography variant="caption" color="text.secondary">
                                {isAiMode ? "IA Générative" : "Manuelle"}
                            </Typography>
                        </Box>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={isAiMode}
                                    onChange={(e) => setIsAiMode(e.target.checked)}
                                    sx={{
                                        '& .MuiSwitch-switchBase.Mui-checked': {
                                            color: '#667eea',
                                            '&:hover': {
                                                backgroundColor: 'rgba(102, 126, 234, 0.08)',
                                            },
                                        },
                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                            backgroundColor: '#667eea',
                                        },
                                    }}
                                />
                            }
                            label=""
                        />
                    </Box>

                    {isAiMode ? (
                        <>
                            <TextField
                                autoFocus
                                margin="dense"
                                label="Sujet du cours"
                                fullWidth
                                value={aiTopic}
                                onChange={(e) => setAiTopic(e.target.value)}
                                placeholder="Ex: Introduction au Marketing Digital..."
                                InputProps={{
                                    sx: { borderRadius: 2 }
                                }}
                            />
                            <TextField
                                margin="dense"
                                label="Description du cours (IA)"
                                fullWidth
                                multiline
                                rows={3}
                                value={aiDescription}
                                onChange={(e) => setAiDescription(e.target.value)}
                                placeholder="Détaillez le contenu attendu..."
                                sx={{ mt: 2 }}
                                InputProps={{
                                    sx: { borderRadius: 2 }
                                }}
                            />
                            <TextField
                                margin="dense"
                                label="Instructions supplémentaires"
                                fullWidth
                                multiline
                                rows={2}
                                value={aiInstructions}
                                onChange={(e) => setAiInstructions(e.target.value)}
                                placeholder="Ex: Niveau débutant, ton formel, focus sur..."
                                sx={{ mt: 2 }}
                                InputProps={{
                                    sx: { borderRadius: 2 }
                                }}
                            />
                            <Box sx={{ mt: 2, p: 2, borderRadius: 2, bgcolor: 'rgba(102, 126, 234, 0.05)', border: '1px solid rgba(102, 126, 234, 0.1)' }}>
                                <TextField
                                    select
                                    label="Modèle IA"
                                    fullWidth
                                    value={modelChoose}
                                    onChange={(e) => setModelChoose(e.target.value as ModelKey)}
                                    size="small"
                                >
                                    {(Object.keys(listModel) as ModelKey[]).map((key) => (
                                        <MenuItem key={key} value={key}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'flex-start', gap: 2 }}>
                                                <Box sx={{ flex: 1 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{listModel[key].name}</Typography>
                                                        <Chip
                                                            label={listModel[key].model}
                                                            size="small"
                                                            sx={{
                                                                height: 18,
                                                                fontSize: '0.65rem',
                                                                bgcolor: 'rgba(102, 126, 234, 0.1)',
                                                                color: '#667eea',
                                                                fontWeight: 600
                                                            }}
                                                        />
                                                    </Box>
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                        {listModel[key].description}
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5 }}>
                                                        <Typography variant="caption" sx={{ color: '#667eea', fontWeight: 500 }}>
                                                            ⏱ {listModel[key].time}
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                                                            {[...Array(10)].map((_, i) => (
                                                                <Box
                                                                    key={i}
                                                                    sx={{
                                                                        width: 6,
                                                                        height: 6,
                                                                        borderRadius: '50%',
                                                                        bgcolor: i < listModel[key].qualityScore
                                                                            ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                                                            : 'rgba(0,0,0,0.1)',
                                                                        background: i < listModel[key].qualityScore
                                                                            ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                                                            : 'rgba(0,0,0,0.1)',
                                                                    }}
                                                                />
                                                            ))}
                                                        </Box>
                                                    </Box>
                                                </Box>
                                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#667eea', whiteSpace: 'nowrap' }}>
                                                    {listModel[key].price}€
                                                </Typography>
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </TextField>
                                <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 1.5, bgcolor: 'rgba(102, 126, 234, 0.08)' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                                        <Box>
                                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#333' }}>
                                                {listModel[modelChoose].name}
                                                <Chip
                                                    label={listModel[modelChoose].model}
                                                    size="small"
                                                    sx={{ ml: 1, height: 16, fontSize: '0.6rem', bgcolor: '#667eea', color: 'white' }}
                                                />
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                                ⏱ {listModel[modelChoose].time} • Qualité: {listModel[modelChoose].qualityScore}/10
                                            </Typography>
                                        </Box>
                                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#667eea' }}>
                                            ~{listModel[modelChoose].price}€ / cours
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </>
                    ) : (
                        <>
                            <TextField
                                autoFocus
                                margin="dense"
                                label="Titre du cours"
                                fullWidth
                                value={newCourse.title}
                                onChange={(e) => setNewCourse({ ...newCourse, title: e.target.value })}
                                InputProps={{
                                    sx: { borderRadius: 2 }
                                }}
                            />
                            <TextField
                                margin="dense"
                                label="Description courte"
                                fullWidth
                                multiline
                                rows={4}
                                value={newCourse.description}
                                onChange={(e) => setNewCourse({ ...newCourse, description: e.target.value })}
                                sx={{ mt: 2 }}
                                InputProps={{
                                    sx: { borderRadius: 2 }
                                }}
                            />
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 0 }}>
                    <Button onClick={() => setOpen(false)} sx={{ color: 'text.secondary', fontWeight: 600 }}>Annuler</Button>
                    <Button
                        onClick={isAiMode ? handleAiCreate : handleCreate}
                        variant="contained"
                        disabled={creating}
                        sx={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            fontWeight: 700,
                            borderRadius: 2,
                            px: 3,
                            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                        }}
                    >
                        {creating ? <CircularProgress size={24} color="inherit" /> : (isAiMode ? 'Générer' : 'Créer')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* First Delete Confirmation Modal */}
            <Dialog
                open={deleteStep === 1}
                onClose={handleCancelDelete}
                PaperProps={{
                    sx: { borderRadius: 3, maxWidth: 400 }
                }}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'bold', color: 'warning.main' }}>
                    <WarningAmberIcon /> Confirmation
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ mb: 2 }}>
                        Voulez-vous supprimer le cours :
                    </Typography>
                    <Typography sx={{ fontWeight: 'bold', color: '#1a237e', bgcolor: 'rgba(25, 118, 210, 0.1)', p: 1.5, borderRadius: 2 }}>
                        "{courseToDelete?.title}"
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={handleCancelDelete} sx={{ color: 'text.secondary' }}>Annuler</Button>
                    <Button
                        onClick={handleConfirmFirstStep}
                        variant="contained"
                        color="warning"
                        sx={{ textTransform: 'none', fontWeight: 'bold', borderRadius: 2 }}
                    >
                        Continuer
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Second (Final) Delete Confirmation Modal */}
            <Dialog
                open={deleteStep === 2}
                onClose={handleCancelDelete}
                PaperProps={{
                    sx: { borderRadius: 3, maxWidth: 450, border: '2px solid', borderColor: 'error.main' }
                }}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'bold', color: 'error.main' }}>
                    <WarningAmberIcon /> Suppression définitive
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ mb: 2, fontWeight: 600, color: 'error.main' }}>
                        Action irréversible !
                    </Typography>
                    <Typography sx={{ mb: 2 }}>
                        Le contenu complet sera perdu :
                    </Typography>
                    <Typography sx={{ fontWeight: 'bold', color: 'error.main', bgcolor: 'rgba(211, 47, 47, 0.1)', p: 1.5, borderRadius: 2 }}>
                        "{courseToDelete?.title}"
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={handleCancelDelete} sx={{ color: 'text.secondary' }}>Annuler</Button>
                    <Button
                        onClick={handleDeleteCourse}
                        variant="contained"
                        color="error"
                        disabled={deleting}
                        sx={{ textTransform: 'none', fontWeight: 'bold', borderRadius: 2, boxShadow: '0 4px 12px rgba(211, 47, 47, 0.3)' }}
                    >
                        {deleting ? <CircularProgress size={20} color="inherit" /> : 'Supprimer'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CourseList;
